import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, CheckCircle2, ChevronRight, PartyPopper, Rocket, X } from "lucide-react";
import { useSetupCentre } from "@/hooks/useSetupCentre";

interface Props {
  /** Compact spacing for the native/mobile dashboard */
  compact?: boolean;
}

/**
 * Dashboard summary for the Setup Centre.
 * Additive: honours the existing onboarding_enabled / onboarding_dismissed preferences.
 */
export function SetupCentreCard({ compact = false }: Props) {
  const navigate = useNavigate();
  const { loading, visible, tasks, completedCount, totalCount, allRequiredComplete, readiness, dismiss } =
    useSetupCentre();

  if (loading || !visible) return null;

  const nextTasks = tasks.filter((t) => !t.complete).slice(0, 3);

  if (allRequiredComplete && nextTasks.length === 0) {
    return (
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className={compact ? "p-4" : "p-6"}>
          <div className="flex items-start gap-3">
            <PartyPopper className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground">Your account is fully set up</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Readiness score {readiness.score}%. You can revisit the Setup Centre at any time.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate("/setup-centre")}>
                  Setup Centre
                </Button>
                <Button variant="ghost" size="sm" onClick={dismiss}>
                  Hide this
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardContent className={compact ? "space-y-3 p-4" : "space-y-4 p-6"}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div className="shrink-0 rounded-xl bg-primary/10 p-2">
              <Rocket className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-foreground sm:text-lg">Finish setting up TransportMate</h2>
              <p className="text-sm text-muted-foreground">
                {completedCount} of {totalCount} steps complete · Readiness {readiness.score}%
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 shrink-0 p-0"
            aria-label="Hide setup summary"
            onClick={dismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <Progress value={totalCount > 0 ? (completedCount / totalCount) * 100 : 0} className="h-2" />

        <div className="space-y-2">
          {nextTasks.map((task) => (
            <div key={task.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-muted-foreground/40" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{task.title}</p>
                <p className="truncate text-xs text-muted-foreground">{task.description}</p>
              </div>
              <Button size="sm" variant="outline" className="shrink-0" onClick={() => navigate(task.to)}>
                <span className="hidden sm:inline">Continue</span>
                <span className="sm:hidden">Go</span>
                <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>

        <Button variant="ghost" size="sm" className="w-full sm:w-auto" onClick={() => navigate("/setup-centre")}>
          Open Setup Centre
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default SetupCentreCard;
